import { useState, useEffect } from 'react';

import { Box, IconButton, Pagination, PaginationItem, useMediaQuery, useTheme } from '@mui/material';

import { ContentTypeRichText } from '@/graphql/__generated/sdk';
import ContentTypeRichTextComponent from '@/components/ContentTypeRichText';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import { ContentfulBackground } from '@/types';
import { PauseCircleOutlinedIcon, PlayCircleOutlinedIcon } from '@/assets/SVG';

import { alertBannerCtrStyles, alertMessagesCtrStyles, paginationControlIconStyles, paginationItemIconStyles, paginationItemsCtrStyles } from './AlertBanner.styles';


export interface IAlertBanner {
    alertMessages: ContentTypeRichText[];
    dataTestId?: string; // Array of rich text content for dekstop
}

export default function AlertBanner({
    alertMessages = [] as ContentTypeRichText[], // Array of rich text content for dekstop
    dataTestId = "section_alert_banner"
} : Readonly<IAlertBanner>) {

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isPaused) {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % alertMessages.length);
            }
        }, 4000); // Auto-rotate every 4 seconds

        return () => clearInterval(interval);
    }, [alertMessages.length, isPaused]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentIndex(page - 1);
      };

    const currentContent = alertMessages[currentIndex];
    // always use the background color of the first alert message
    // as the background color for the alert banner
    const backGrounColor = alertMessages[0].backgroundColor as ContentfulBackground;
    const isDarkBackground = !!backGrounColor?.toLowerCase().includes('dark');
    const paginationIconColorSelected = isDarkBackground ? 'neutrals.white' : 'secondary.main';
    const responsiveTypeVariant = isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook';
    const typographyColor = isDarkBackground ? 'text.light' : 'text.interactive';



    return (
        <Box
            data-testid={dataTestId}
            sx={alertBannerCtrStyles(theme, backGrounColor)}
        >
            <ContentTypeRichTextComponent 
                content={currentContent.content}
                disablePaddingOn={['Top', 'Bottom']}
                disableGutters
                centerAlign
                backgroundColor={backGrounColor}
                contentMaxWidth={'none'}
                containerSx={alertMessagesCtrStyles}
                docProps={{ variant: responsiveTypeVariant, color: typographyColor }}
                dataTestId={`alert_banner_content_${currentIndex}`}
            />
            { alertMessages.length > 1 && (
                <Box sx={paginationItemsCtrStyles} data-testid="alert_banner_pagination_ctr">
                    <Pagination
                        count={alertMessages.length}
                        page={currentIndex + 1}
                        onChange={handlePageChange}
                        hidePrevButton
                        hideNextButton
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={paginationItemIconStyles}
                                page={
                                    <TAGSvgIcon
                                        size={8}
                                        icon="CircleRounded"
                                        htmlColor={item.page === currentIndex + 1 ? paginationIconColorSelected : 'neutrals.primary'}
                                    />
                                }
                                data-testid={`alert_banner_pagination_${item.page || 0}`}
                            />
                        )}
                    />
                    {isPaused ? (
                            <IconButton data-testid="alert_banner_play" onClick={() => setIsPaused((prev) => !prev)} sx={paginationControlIconStyles(isDarkBackground)}>
                                <PlayCircleOutlinedIcon />
                            </IconButton>
                        ) : (
                            <IconButton data-testid="alert_banner_pause" onClick={() => setIsPaused((prev) => !prev)} sx={paginationControlIconStyles(isDarkBackground)}>
                                <PauseCircleOutlinedIcon/>
                            </IconButton>
                        )}
                </Box>
            )}
        </Box>
    );
}